// src/lib/siteColors.ts
// Website colors: the curated palette, the tokens the booking site renders, and
// the WCAG accessibility grade shown in Website Basics. User colors are never
// changed; only text colors are derived.
//
// Twin copy: help-from-sky-native/src/domain/website/siteColors.ts. Keep both files identical and
// dependency-free — both repos run the same tests (docs/specs/website-basics-colors-spec.md §4).

export type ColorSlot = 'background' | 'primary' | 'accent';

export interface SiteColorInputs {
  background: string;
  primary: string;
  accent: string;
}

export type SwatchName =
  | 'white' | 'cream' | 'blush' | 'sageMist' | 'stone' | 'midnight'
  | 'indigo' | 'rose' | 'sage' | 'terracotta' | 'plum' | 'ink'
  | 'gold' | 'petal' | 'mint' | 'butter' | 'peach' | 'lilac';

/** A palette swatch. `light` is used on light backgrounds, `dark` on dark ones. */
export interface Swatch {
  name: SwatchName; // i18n key under website.basics.swatch
  light: string;
  dark: string;
}

const same = (name: SwatchName, hex: string): Swatch => ({ name, light: hex, dark: hex });

export const SITE_SWATCHES: Record<ColorSlot, readonly Swatch[]> = {
  background: [
    same('white', '#FFFFFF'),
    same('cream', '#FAF7F2'),
    same('blush', '#F7EDEE'),
    same('sageMist', '#EEF2EC'),
    same('stone', '#2A2826'),
    same('midnight', '#16202E'),
  ],
  primary: [
    { name: 'indigo', light: '#3730A3', dark: '#A5B4FC' },
    { name: 'rose', light: '#7F2E44', dark: '#EBA8B8' },
    { name: 'sage', light: '#36503C', dark: '#A9C4AD' },
    { name: 'terracotta', light: '#7A361B', dark: '#E8AC90' },
    { name: 'plum', light: '#5B3F69', dark: '#CDB4DA' },
    { name: 'ink', light: '#1F2937', dark: '#CBD5E1' },
  ],
  accent: [
    same('gold', '#E2B65A'),
    same('petal', '#F2B8C6'),
    same('mint', '#A8DCC8'),
    same('butter', '#F4E19C'),
    same('peach', '#F6B48F'),
    same('lilac', '#C9B8E8'),
  ],
};

export const SITE_COLOR_DEFAULTS: SiteColorInputs = {
  background: '#FFFFFF',
  primary: '#3730A3',
  accent: '#E2B65A',
};

// ── Primitives (WCAG 2.1) ─────────────────────────────────────────────────────

const DARK_TEXT = '#111111';
const LIGHT_TEXT = '#FFFFFF';
export const AA = 4.5;
export const AAA = 7;

/** `#RGB` or `#RRGGBB` in any case → uppercase `#RRGGBB`; anything else → null. */
export function normalizeHex(input: string | null | undefined): string | null {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((input ?? '').trim());
  if (!match) return null;
  const digits = match[1].length === 3 ? match[1].replace(/./g, (c) => c + c) : match[1];
  return `#${digits.toUpperCase()}`;
}

function toRgb(hex: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}

function toHex(rgb: number[]): string {
  return `#${rgb
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function ratioOfLuminances(la: number, lb: number): number {
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export function contrastRatio(a: string, b: string): number {
  return ratioOfLuminances(relativeLuminance(a), relativeLuminance(b));
}

/** Linear interpolation per sRGB channel. Only for derived neutrals. */
export function mix(a: string, b: string, t: number): string {
  const ca = toRgb(a);
  const cb = toRgb(b);
  return toHex(ca.map((v, i) => v + (cb[i] - v) * t));
}

function toHsl(hex: string): [number, number, number] {
  const [r, g, b] = toRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h /= 6;
  return [h, s, l];
}

function fromHsl(h: number, s: number, l: number): string {
  if (s === 0) return toHex([l * 255, l * 255, l * 255]);
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return toHex([channel(h + 1 / 3) * 255, channel(h) * 255, channel(h - 1 / 3) * 255]);
}

const DARK_TEXT_LUMINANCE = relativeLuminance(DARK_TEXT);
const LIGHT_TEXT_LUMINANCE = relativeLuminance(LIGHT_TEXT);

/** Contrast of the better text color (near-black or white) on a fill of this luminance. */
function bestTextRatio(fillLuminance: number): number {
  return Math.max(
    ratioOfLuminances(DARK_TEXT_LUMINANCE, fillLuminance),
    ratioOfLuminances(LIGHT_TEXT_LUMINANCE, fillLuminance),
  );
}

/** Whichever of near-black or white reads better on `bg`. */
export function textOn(bg: string): string {
  const l = relativeLuminance(bg);
  return ratioOfLuminances(DARK_TEXT_LUMINANCE, l) >= ratioOfLuminances(LIGHT_TEXT_LUMINANCE, l) ? DARK_TEXT : LIGHT_TEXT;
}

export function isDark(bg: string): boolean {
  return textOn(bg) === LIGHT_TEXT;
}

export type LightnessDirection = 'darker' | 'lighter' | 'either';

/** The nearest same-hue shade that passes, stepping lightness by 0.01 (darker first). */
export function nearestLightness(
  color: string,
  passes: (candidate: string) => boolean,
  direction: LightnessDirection,
): string | null {
  const [h, s, start] = toHsl(color);
  for (let k = 1; k <= 100; k++) {
    const step = k / 100;
    const tries =
      direction === 'darker' ? [start - step] : direction === 'lighter' ? [start + step] : [start - step, start + step];
    for (const lightness of tries) {
      if (lightness < 0 || lightness > 1) continue;
      const candidate = fromHsl(h, s, lightness);
      if (passes(candidate)) return candidate;
    }
  }
  return null;
}

// ── Tokens ────────────────────────────────────────────────────────────────────

export interface SiteColorTokens {
  background: string;
  text: string;
  mutedText: string;
  primaryFill: string;
  onPrimary: string;
  /** A border for primary buttons that would otherwise vanish into the page; null when not needed. */
  primaryOutline: string | null;
  link: string;
  accent: string;
  onAccent: string;
  border: string;
}

export function deriveSiteColors({ background, primary, accent }: SiteColorInputs): SiteColorTokens {
  const text = textOn(background);

  let mutedText = mix(text, background, 0.4);
  if (contrastRatio(mutedText, background) < AA) {
    mutedText =
      nearestLightness(
        mutedText,
        (c) => contrastRatio(c, background) >= AA,
        text === DARK_TEXT ? 'darker' : 'lighter',
      ) ?? text;
  }

  return {
    background,
    text,
    mutedText,
    primaryFill: primary,
    onPrimary: textOn(primary),
    primaryOutline: contrastRatio(primary, background) >= 3 ? null : text,
    link: primary,
    accent,
    onAccent: textOn(accent),
    border: mix(text, background, 0.88),
  };
}

// ── Accessibility grade ───────────────────────────────────────────────────────

export type ContrastCheckId = 'text' | 'button' | 'link' | 'badge';
export type ContrastLevel = 'AAA' | 'AA' | 'fail';
export type GradeTitle = 'excellent' | 'good' | 'poor';

export interface ContrastCheck {
  id: ContrastCheckId;
  foreground: string;
  background: string;
  ratio: number;
  level: ContrastLevel;
}

export interface AccessibilityGrade {
  score: number;
  title: GradeTitle;
  aaaCount: number;
  failCount: number;
  checks: ContrastCheck[];
}

function contrastLevel(ratio: number): ContrastLevel {
  if (ratio >= AAA) return 'AAA';
  if (ratio >= AA) return 'AA';
  return 'fail';
}

function contrastPoints(ratio: number): number {
  if (ratio >= AAA) return 25;
  if (ratio >= AA) return 20 + (5 * (ratio - AA)) / (AAA - AA);
  return 0;
}

export function accessibilityGrade(tokens: SiteColorTokens): AccessibilityGrade {
  const pairs: [ContrastCheckId, string, string][] = [
    ['text', tokens.text, tokens.background],
    ['button', tokens.onPrimary, tokens.primaryFill],
    ['link', tokens.link, tokens.background],
    ['badge', tokens.onAccent, tokens.accent],
  ];
  const checks = pairs.map(([id, foreground, background]): ContrastCheck => {
    const ratio = contrastRatio(foreground, background);
    return { id, foreground, background, ratio, level: contrastLevel(ratio) };
  });

  const aaaCount = checks.filter((c) => c.level === 'AAA').length;
  const failCount = checks.filter((c) => c.level === 'fail').length;
  // floor, so a set that is not all AAA can never show 100
  const score = Math.floor(checks.reduce((sum, c) => sum + contrastPoints(c.ratio), 0));
  const title: GradeTitle = failCount > 0 ? 'poor' : aaaCount === checks.length ? 'excellent' : 'good';

  return { score, title, aaaCount, failCount, checks };
}

// ── Suggestions ───────────────────────────────────────────────────────────────

/** Which checks a slot's notice covers. */
export const SLOT_CHECKS: Record<ColorSlot, readonly ContrastCheckId[]> = {
  background: ['text'],
  primary: ['button', 'link'],
  accent: ['badge'],
};

/** The nearest same-hue shade that fixes the slot's failing checks; null when they pass or nothing fixes them. */
export function suggestShade(slot: ColorSlot, inputs: SiteColorInputs): string | null {
  const backgroundLuminance = relativeLuminance(inputs.background);
  const passes = (c: string) => {
    const l = relativeLuminance(c);
    if (bestTextRatio(l) < AA) return false;
    return slot !== 'primary' || ratioOfLuminances(l, backgroundLuminance) >= AA;
  };
  const current = inputs[slot];
  return passes(current) ? null : nearestLightness(current, passes, 'either');
}

// ── Palette helpers ───────────────────────────────────────────────────────────

/** The slot's swatches as shown for this background, e.g. light or dark primary shades. */
export function swatchesFor(slot: ColorSlot, background: string): { name: SwatchName; hex: string }[] {
  const dark = isDark(background);
  return SITE_SWATCHES[slot].map((s) => ({ name: s.name, hex: dark ? s.dark : s.light }));
}

/** The swatch a stored value belongs to (either shade), or null for a custom color. */
export function findSwatch(slot: ColorSlot, hex: string): Swatch | null {
  const value = normalizeHex(hex);
  return SITE_SWATCHES[slot].find((s) => s.light === value || s.dark === value) ?? null;
}

/** A palette primary switched to the shade for this background; custom colors are returned unchanged. */
export function primaryForBackground(primary: string, background: string): string {
  const swatch = findSwatch('primary', primary);
  if (!swatch) return primary;
  return isDark(background) ? swatch.dark : swatch.light;
}
